import { CoiSection } from '@/features/coi/types/coi.types';
import PermissionClassification from '@/util/enums/PermissionClassification';

/**
 * PBI #7751 — the mapping from what the COI UI shows to the eight permissions #7749 registered.
 *
 * IDS: 39-46, NOT the 38-45 in #7751's Technical Document. That document carries the original draft
 * numbering; 38 was already `MasterDataResync`, so #7749 re-agreed the range upward. Gating
 * Screenings on 38 would hand COI to every holder of MasterDataResync. The contract test asserts
 * these by VALUE for exactly that reason.
 *
 * These tables are the single source the whole module gates from — the nav entry, the sidebar, the
 * Registers tab strip and the route guards all read them, so a section added to the UI without an
 * entry here is caught by the contract test rather than shipping ungated.
 */
export const COI_PERMISSIONS: PermissionClassification[] = [
  PermissionClassification.CoiScreenings,
  PermissionClassification.CoiCases,
  PermissionClassification.CoiDisclosures,
  PermissionClassification.CoiRegistersPeople,
  PermissionClassification.CoiRegistersApprovedSuppliers,
  PermissionClassification.CoiRegistersGifts,
  PermissionClassification.CoiCompanyNetwork,
  PermissionClassification.CoiSettings
];

/**
 * The three register permissions, in the order their tabs are displayed. Order is load-bearing:
 * AC5's "default tab is the first permitted" has to be deterministic, and deriving it from the
 * display order rather than from the order permissions arrive in is what makes it so.
 */
export const COI_REGISTER_PERMISSIONS: PermissionClassification[] = [
  PermissionClassification.CoiRegistersPeople,
  PermissionClassification.CoiRegistersApprovedSuppliers,
  PermissionClassification.CoiRegistersGifts
];

export const COI_REGISTER_TABS = ['people', 'approved-suppliers', 'gifts'] as const;

export type CoiRegisterTab = (typeof COI_REGISTER_TABS)[number];

/**
 * One permission per register tab. 'people' is a single permission covering both the employee and
 * board/exec registers — US-36 merged those two into one presentational tab, and #7749 issued one
 * permission (CoiRegistersPeople) for the pair rather than one each.
 */
export const COI_REGISTER_TAB_PERMISSIONS: Record<
  CoiRegisterTab,
  PermissionClassification
> = {
  people: PermissionClassification.CoiRegistersPeople,
  'approved-suppliers': PermissionClassification.CoiRegistersApprovedSuppliers,
  gifts: PermissionClassification.CoiRegistersGifts
};

/**
 * The route each COI section lives at. Single source: `COI_NAV_GROUPS` builds its links from this,
 * and the navigation entry resolves its landing path from it, so the two cannot drift. Before
 * #7751 the nav entry hard-coded `/myoffice/coi/check-runs`, which AC2 rules out — a user holding
 * only Cases must land on Cases, not on a Screenings route they cannot open.
 */
export const COI_SECTION_PATHS: Record<CoiSection, string> = {
  checks: '/myoffice/coi/check-runs',
  cases: '/myoffice/coi/cases',
  disclosures: '/myoffice/coi/disclosures',
  registers: '/myoffice/coi/registers',
  network: '/myoffice/coi/network',
  settings: '/myoffice/coi/settings'
};

/**
 * Sections in the order the sidebar lists them (Overview, Work, Data, Admin). "First permitted",
 * which AC2 relies on for the landing route, is resolved in exactly this order — so what the nav
 * entry opens is always the topmost item the user can actually see in the sidebar.
 */
export const COI_SECTION_ORDER: CoiSection[] = [
  'checks',
  'cases',
  'disclosures',
  'registers',
  'network',
  'settings'
];

/**
 * Six sidebar sections, eight permissions. The asymmetry is Registers, which is reachable with ANY
 * of its three — hence `PermissionClassification[]` per section rather than a single value. Every
 * other section is a one-element list so callers never need to special-case it.
 */
export const COI_SECTION_PERMISSIONS: Record<CoiSection, PermissionClassification[]> = {
  checks: [PermissionClassification.CoiScreenings],
  cases: [PermissionClassification.CoiCases],
  disclosures: [PermissionClassification.CoiDisclosures],
  registers: COI_REGISTER_PERMISSIONS,
  network: [PermissionClassification.CoiCompanyNetwork],
  settings: [PermissionClassification.CoiSettings]
};
